import React, { useState } from 'react';
import { useQuery, useMutation } from 'react-query';
import {
  Button,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogContent,
  DialogBody,
  DialogActions,
  Field,
  Input,
  Text,
  Spinner,
  makeStyles,
} from '@fluentui/react-components';
import {
  Edit24Regular,
  Key24Regular,
  Person24Regular,
  Calendar24Regular,
} from '@fluentui/react-icons';
import { authApi, AdminUser } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import PageHeader from '../components/PageHeader.tsx';
import StatusBadge from '../components/StatusBadge.tsx';
import { useToast } from '../components/Toast.tsx';

const MOBILE = '@media (max-width: 768px)';

const useStyles = makeStyles({
  section: {
    marginBottom: 'var(--space-xl)',
  },
  sectionTitle: {
    fontSize: '16px',
    fontWeight: '600',
    color: 'var(--color-text)',
    marginBottom: 'var(--space-md)',
    display: 'block',
  },
  card: {
    backgroundColor: 'var(--color-surface)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-md) var(--space-lg)',
    marginBottom: 'var(--space-sm)',
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-md)',
    flexWrap: 'wrap',
    [MOBILE]: {
      padding: 'var(--space-md)',
    },
  },
  cardIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-background)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--color-text-secondary)',
    flexShrink: 0,
  },
  cardContent: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  cardLabel: {
    fontSize: '12px',
    color: 'var(--color-text-secondary)',
  },
  cardValue: {
    fontSize: '14px',
    fontWeight: '500',
    color: 'var(--color-text)',
    overflowWrap: 'anywhere',
  },
});

const AdminSettings: React.FC = () => {
  const s = useStyles();
  const toast = useToast();
  const { user } = useAuth();

  const [showPasswordDialog, setShowPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const { data: currentAdmin, isLoading } = useQuery<AdminUser>({
    queryKey: ['currentAdmin'],
    queryFn: () => authApi.getCurrentAdmin(),
  });

  const changePasswordMutation = useMutation({
    mutationFn: authApi.changePassword,
    onSuccess: () => {
      setShowPasswordDialog(false);
      setCurrentPassword(''); setNewPassword(''); setConfirmPassword('');
      toast.success('Password changed successfully');
    },
    onError: () => toast.error('Failed to change password'),
  });

  const handleChangePassword = async () => {
    if (!currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim()) return;
    if (newPassword !== confirmPassword) { toast.warning('New passwords do not match'); return; }
    if (newPassword.length < 6) { toast.warning('Password must be at least 6 characters'); return; }
    setIsChangingPassword(true);
    try { await changePasswordMutation.mutateAsync({ current_password: currentPassword, new_password: newPassword }); }
    finally { setIsChangingPassword(false); }
  };

  if (isLoading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 'var(--space-2xl)' }}><Spinner size="large" /></div>;

  return (
    <div style={{ maxWidth: '640px' }}>
      <PageHeader title="Account Settings" subtitle="Manage your profile and security" />

      {/* Profile */}
      <div className={s.section}>
        <Text className={s.sectionTitle}>Profile Information</Text>

        <div className={s.card}>
          <div className={s.cardIcon}><Person24Regular /></div>
          <div className={s.cardContent}>
            <span className={s.cardLabel}>Full Name</span>
            <span className={s.cardValue}>{currentAdmin?.full_name || user?.full_name || 'Not set'}</span>
          </div>
        </div>

        <div className={s.card}>
          <div className={s.cardIcon}><Person24Regular /></div>
          <div className={s.cardContent}>
            <span className={s.cardLabel}>Email Address</span>
            <span className={s.cardValue}>{currentAdmin?.email || user?.email || 'Not set'}</span>
          </div>
        </div>

        <div className={s.card}>
          <div className={s.cardIcon}><Person24Regular /></div>
          <div className={s.cardContent}>
            <span className={s.cardLabel}>Account Status</span>
            <StatusBadge status={currentAdmin?.is_active ? 'active' : 'inactive'} />
          </div>
        </div>
      </div>

      {/* Security */}
      <div className={s.section}>
        <Text className={s.sectionTitle}>Security</Text>

        <div className={s.card}>
          <div className={s.cardIcon}><Key24Regular /></div>
          <div className={s.cardContent}>
            <span className={s.cardLabel}>Password</span>
            <span className={s.cardValue}>••••••••</span>
          </div>
          <Dialog open={showPasswordDialog} onOpenChange={(_, data) => setShowPasswordDialog(data.open)}>
            <DialogTrigger disableButtonEnhancement>
              <Button appearance="secondary" size="small" icon={<Edit24Regular />}>Change</Button>
            </DialogTrigger>
            <DialogSurface style={{ width: '90vw', maxWidth: '480px' }}>
              <DialogBody>
                <DialogTitle>Change Password</DialogTitle>
                <DialogContent>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-md)' }}>
                    <Field label="Current Password" required><Input type="password" value={currentPassword} onChange={(_, data) => setCurrentPassword(data.value)} placeholder="Enter current password" /></Field>
                    <Field label="New Password" required><Input type="password" value={newPassword} onChange={(_, data) => setNewPassword(data.value)} placeholder="Enter new password" /></Field>
                    <Field label="Confirm New Password" required><Input type="password" value={confirmPassword} onChange={(_, data) => setConfirmPassword(data.value)} placeholder="Confirm new password" /></Field>
                  </div>
                </DialogContent>
                <DialogActions>
                  <DialogTrigger disableButtonEnhancement><Button appearance="secondary">Cancel</Button></DialogTrigger>
                  <Button appearance="primary" onClick={handleChangePassword} disabled={isChangingPassword || !currentPassword.trim() || !newPassword.trim() || !confirmPassword.trim() || newPassword !== confirmPassword || newPassword.length < 6}>
                    {isChangingPassword ? 'Changing...' : 'Change Password'}
                  </Button>
                </DialogActions>
              </DialogBody>
            </DialogSurface>
          </Dialog>
        </div>
      </div>

      {/* Account Info */}
      <div className={s.section}>
        <Text className={s.sectionTitle}>Account Information</Text>

        <div className={s.card}>
          <div className={s.cardIcon}><Calendar24Regular /></div>
          <div className={s.cardContent}>
            <span className={s.cardLabel}>Member Since</span>
            <span className={s.cardValue}>
              {(currentAdmin as any)?.created_at
                ? new Date((currentAdmin as any).created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
                : 'Unknown'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
